import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import StatusBadge from "../../../../components/common/StatusBadge";
import { ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL } from "../../salesStatusMaps";
import SalespersonPicker from "../../components/SalespersonPicker";
import { useListEnquiriesQuery } from "../../salesApi";
import { useCanCreateEnquiry, useCanViewAllSales } from "../../useSalesPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "POSTPONED", label: "Postponed" },
  { value: "LOST", label: "Lost" },
];

const money = (n) => Number(n || 0).toFixed(2);

const EnquiryCompactList = () => {
  const canCreate = useCanCreateEnquiry();
  // Only offered to someone who can see other people's sales at all - without
  // Sales.ViewAll the list is already scoped to your own records, so filtering
  // by another salesperson could only ever return nothing.
  const canViewAll = useCanViewAllSales();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [salesperson, setSalesperson] = useState(null);
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // The box has always promised "customer OR enquiry number" but only ever
  // sent customerName, so typing a number silently matched nothing. The proc
  // has had an @EnquiryNumber parameter all along; route to it when the text
  // looks like an enquiry number (SE-000034, se000034, or a bare number) and
  // fall back to the customer-name LIKE otherwise. @EnquiryNumber is an exact
  // match server-side, hence the zero-padding to the SE-000000 shape.
  const looksLikeEnquiryNumber = /^(se-?)?\d{1,6}$/i.test(search);
  const enquiryNumberTerm = looksLikeEnquiryNumber
    ? `SE-${search.replace(/^se-?/i, "").padStart(6, "0")}`
    : undefined;

  const { data, isLoading } = useListEnquiriesQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    customerName: enquiryNumberTerm ? undefined : search || undefined,
    enquiryNumber: enquiryNumberTerm,
    salespersonEmployeeRecordGuid: salesperson?.value || undefined,
    pageSize: 200,
  });

  const items = data?.items || [];

  return (
    <MasterDetailListShell
      title="Sales Enquiries"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.salesEnquiryNew)}>
            <PlusCircle className="me-2" size={16} />
            New Enquiry
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search customer or enquiry number"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {/* Find every enquiry belonging to one salesperson, by employee code
              or name (Batch153). The salesperson is an employee, not a login
              (Batch152), so this searches employees - the same code-then-Enter
              box used on Employees, Vehicles and the technician pickers. */}
          {canViewAll && (
            <div className="mt-2">
              <label className="form-label text-muted mb-1" style={{ fontSize: 11 }}>
                Salesperson (employee)
              </label>
              <SalespersonPicker value={salesperson} onChange={setSalesperson} />
            </div>
          )}
        </>
      }
      isLoading={isLoading}
      emptyLabel="No enquiries match these filters."
      items={items}
      getKey={(e) => e.recordGuid}
      resetKey={`${search}|${status}|${salesperson?.value || ""}|${selectedBranchId}`}
      renderItem={(e) => {
        const isSelected = String(e.recordGuid) === String(selectedRecordGuid);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.salesEnquiryDetail(e.recordGuid))}
            avatarLabel={(e.customerName || "?").trim().slice(0, 2).toUpperCase()}
            title={
              // Customer leads, enquiry number is the detail line (2026-09-12).
              <>
                {e.customerName || e.enquiryNumber} <span className="text-muted ms-1">{money(e.totalAmount || e.otherServicesAmount)}</span>
              </>
            }
            subtitle={<>{e.enquiryNumber} &middot; {e.branchCode}{e.salespersonName ? <> &middot; {e.salespersonName}</> : null}</>}
            badge={
              <span className="me-2">
                <StatusBadge status={e.status} classMap={ENQUIRY_STATUS_CLASS} labelMap={ENQUIRY_STATUS_LABEL} />
              </span>
            }
            actions={
              <button
                type="button"
                className="btn btn-link p-1"
                title="View"
                onClick={(ev) => {
                  ev.stopPropagation();
                  navigate(paths.salesEnquiryDetail(e.recordGuid));
                }}
              >
                <Eye size={15} />
              </button>
            }
          />
        );
      }}
    />
  );
};

export default EnquiryCompactList;
